import $ from 'jquery';
import Analytics from '../analytics';
import Environment from '../environment';
import LinkHandler from '../linkHandler';
import { getLogger } from '../logging/logger';

const logger = getLogger('MoneyBadger');

class Comcast extends LinkHandler {
	constructor() {
		logger.log('[constructor]');

		super();

		if (Environment.isComcastSetTopBox()) {
			$(document).one('onMoneyBadgerReady', this.onMoneyBadgerReady.bind(this));
			this.loadSdk();
		} else {
			this.trigger('ready');
		}
	}

	/**
	 * We are loading the money-badger library dynamically because it tries to
	 * create a global variable and that does not play nice with the normal
	 * approach of importing via Webpack. While we could add the script tag into
	 * our index.html template, dynamically adding it here will only add it if
	 * the MoneyBadger library is needed.
	 */
	loadSdk() {
		logger.log('[loadSdk]');

		const script = document.createElement('script');
		script.async = true;
		script.src = `https://cdn.thor.comcast.com/sdk/money-badger.min.js`;
		document.head.appendChild(script);
	}

	get linkPattern() {
		return /^xre:\/\//;
	}

	onMoneyBadgerReady() {
		logger.log(`[${this.onMoneyBadgerReady.name}]`);

		this.trigger('ready');
		window.$badger.launchCompletedMetricsHandler();
	}

	/**
	 * @param {string} url
	 */
	handleLink(url) {
		logger.log(`[${this.handleLink.name}]`, { url });

		if (!Environment.isComcastSetTopBox()) {
			logger.warn('Deep links only work on SetTopBox');
			return;
		}

		if (!Environment.isInHub()) {
			Analytics.sendEvent('MoneyBadgerDeeplink', { url, status: 'pending' });

			window.$badger
				.deeplink(url)
				.success(() => {
					Analytics.sendEvent('MoneyBadgerDeeplink', { status: 'success', url });
				})
				.failure((/** @type {any} */ error) => {
					logger.error('[Deep link failed]', error);
					Analytics.sendEvent('MoneyBadgerDeeplink-Error', {
						status: 'fail',
						url,
						error: JSON.stringify({ error }),
					});
				});
		} else {
			// Send deeplink up to the hub, which will trigger it
			let { origin } = window.location;
			if (Environment.isLocal()) {
				// Grab the port from referrer
				const [, referrerOrigin] = /(https?:\/\/localhost:\d+)/.exec(
					new URL(document.referrer).origin,
				);
				origin = referrerOrigin;
			}
			window.parent.postMessage(url, origin);
		}
	}

	getUserInfo() {
		logger.log(`[${this.getUserInfo.name}]`);

		if (!window.$badger.active()) {
			Analytics.sendEvent('MoneyBadgerGetUserInfo-Error', { error: '$badger inactive' });
			return Promise.reject(new Error(`[$badger inactive]`));
		}

		return new Promise((resolve, reject) => {
			window.$badger
				.info()
				.success((/** @type {any} */ info) => resolve(info))
				.failure((/** @type {Error} */ error) => {
					logger.error(`[$badger info]: ${error}`);
					reject(error);
				});
		});
	}

	/**
	 * @param {string} [headerText]
	 * @param {string} [selectNoneText]
	 */
	promptEmailSignUp(headerText, selectNoneText) {
		logger.log(`[${this.promptEmailSignUp.name}]`, headerText, selectNoneText);

		return new Promise((resolve, reject) => {
			if (!window.$badger.active()) {
				reject(new Error('$badger inactive'));
				return;
			}

			window.$badger
				.userPreferences()
				.promptSignUpEmail({
					headerText: headerText,
					selectNoneText: selectNoneText,
				})
				.success((/** @type {{ status: string; data: { email: any; }; }} */ response) => {
					if (response.status === 'SUCCESS') {
						resolve(response.data.email);
					} else {
						logger.error('[PromptEmailSignUp Error]', { response });
						reject(new Error('PromptEmailSignUp Error'));
					}
				})
				.failure(reject);
		});
	}

	/**
	 * @param {string} headerText
	 * @param {string} selectNoneText
	 */
	promptEmailSignIn(headerText, selectNoneText) {
		logger.log(`[${this.promptEmailSignIn.name}]`, headerText, selectNoneText);

		window.$badger.userPreferences().promptSignInEmail({
			headerText: headerText,
			selectNoneText: selectNoneText,
		});
	}

	getAppAuth() {
		logger.log(`[${this.getAppAuth.name}]`);

		return new Promise((resolve, reject) => {
			window.$badger.getAppAuth().get().success(resolve).failure(reject);
		});
	}

	/**
	 * @param {string} userId
	 * @param {object} data
	 */
	upsertAppAuth(userId, data) {
		logger.log(`[${this.upsertAppAuth.name}]`, userId, data);

		return new Promise((resolve, reject) => {
			this.getAppAuth().upsert(userId, data).success(resolve).failure(reject);
		});
	}

	deleteAppAuth() {
		logger.log(`[${this.deleteAppAuth.name}]`);
		logger.warn('Disableing $badger.deleteAppAuth until further testing');
		return Promise.reject(
			new Error('Disabled access to $badger.deleteAppAuth until further testing'),
		);
	}

	/**
	 * @param {string} clientId
	 */
	getBearerToken(clientId) {
		logger.log(`[${this.getBearerToken.name}]`);

		return new Promise((resolve, reject) => {
			window.$badger.getBearerToken(clientId).success(resolve).failure(reject);
		});
	}

	// displayError will always exit after user presses a button
	/**
	 * @param {string} title
	 * @param {string} body
	 */
	displayError(title, body) {
		logger.log(`[${this.displayError.name}]`, title, body);
		window.$badger.displayError(title, body);
	}

	shutdown() {
		logger.log(`[${this.shutdown.name}]`);
		window.$badger.shutdown();
	}

	isActive() {
		logger.log(`[${this.isActive.name}]`);
		const isActive = window.$badger.active();
		if (!isActive) {
			logger.error('[$badger inactive]');
		}
		return isActive;
	}

	/**
	 * If MoneyBadger fails to initialize, it will do so silently. This method will return
	 * the service object to be used which indicates if MoneyBadger is supported or not by this device.
	 */
	getMoneyBadgerService() {
		logger.log(`[${this.getMoneyBadgerService.name}]`);
		return new Promise((resolve) => {
			const testService = (/** @type {any} */ service) => {
				if (service) {
					resolve(true);
				} else {
					const msg = '[MISSING "com.comcast.BridgeObject_1" service]';
					logger.error(msg);
					resolve(undefined);
				}
			};

			/* Find the Bridge Object */
			if (typeof ServiceManager !== 'undefined' && window.ServiceManager != null) {
				if ('version' in window.ServiceManager) {
					/** WPE */
					logger.log('[Detected WPE browser]');
					window.ServiceManager.getServiceForJavaScript(
						'com.comcast.BridgeObject_1',
						testService,
					);
				} else {
					/** QT */
					logger.log('[Detected QT browser]');
					const createService =
						window.ServiceManager.createService ||
						window.ServiceManager.getServiceForJavaScript;
					testService(createService('com.comcast.BridgeObject_1'));
				}
			} else {
				const msg = `[${Comcast.name} Failed to find ServiceManager]`;
				logger.error(msg);
				resolve();
			}
		});
	}
}

export default new Comcast();
